import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Form, Icon, Message, Modal, Tab } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { usePopup } from '../../lib/popup';

import InfoPopup from '../InfoPopup';
import DeleteStep from '../DeleteStep';
import selectors from '../../selectors';

import styles from './PointSystemModal.module.scss';

// Константа для представления бесконечности в базе данных
const INFINITY_VALUE = 1000000000;

// Вспомогательные функции для работы с бесконечностью
const isInfinity = (value) => value === INFINITY_VALUE || value === INFINITY_VALUE.toString();
const toInfinity = () => INFINITY_VALUE;
const fromInfinity = (value) => (isInfinity(value) ? null : value);

// SVG иконка бесконечности
const InfinityIcon = React.memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={styles.infinityIcon}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    strokeWidth="2"
    stroke="currentColor"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" />
    <path d="M9.828 9.172a4 4 0 1 0 0 5.656 a10 10 0 0 0 2.172 -2.828a10 10 0 0 1 2.172 -2.828 a4 4 0 1 1 0 5.656a10 10 0 0 1 -2.172 -2.828a10 10 0 0 0 -2.172 -2.828" />
  </svg>
));

InfinityIcon.propTypes = {};

// Компонент свитчера для бесконечности
const InfinityToggle = React.memo(({ checked, onChange }) => (
  <div className={styles.infinityToggle} title="Бесконечное значение">
    <label className={styles.toggleLabel}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className={styles.toggleInput}
      />
      <span className={styles.toggleSlider} />
    </label>
  </div>
));

InfinityToggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SectionInfo = React.memo(({ title }) => {
  const InfoPopupData = usePopup(InfoPopup);

  return (
    <InfoPopupData title={title} content={title}>
      <button type="button" className={styles.infoButton}>
        <Icon name="info circle" />
      </button>
    </InfoPopupData>
  );
});

SectionInfo.propTypes = {
  title: PropTypes.string.isRequired,
};

const PointSystemModal = React.memo(
  ({
    open,
    pointSystem,
    dampingCoefficients,
    employeePlans,
    onEmployeePlansFetch,
    onEmployeePlanCreate,
    onEmployeePlanUpdate,
    onEmployeePlanDelete,
    onContentViewPlanGetOrCreate,
    onContentViewPlanUpdate,
    onContentCardDefaultsFetch,
    onContentCardDefaultCreate,
    onContentCardDefaultUpdate,
    onContentCardDefaultDelete,
    onDampingCoefficientsFetch,
    onDampingCoefficientCreate,
    onDampingCoefficientUpdate,
    onDampingCoefficientDelete,
    onClose,
  }) => {
    const [draftsById, setDraftsById] = useState({});
    const [contentCardDefaultDraftsById, setContentCardDefaultDraftsById] = useState({});
    const [contentViewPlanYear, setContentViewPlanYear] = useState(() => new Date().getFullYear());
    const [contentViewPlanMonth, setContentViewPlanMonth] = useState(1);
    const [contentViewPlanForm, setContentViewPlanForm] = useState(() => ({
      photo: 0,
      video: 0,
      caseCount: 0,
    }));
    const [dampingDraftsById, setDampingDraftsById] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [dampingValidationErrors, setDampingValidationErrors] = useState({});
    const [dampingConflictErrors, setDampingConflictErrors] = useState({});
    const [showValidationMessage, setShowValidationMessage] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null); // { type: 'success' | 'error', text: string }
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const [deletedContentCardDefaults, setDeletedContentCardDefaults] = useState(new Set());
    const [deletedDampingCoefficients, setDeletedDampingCoefficients] = useState(new Set());
    const [deletedEmployeePlans, setDeletedEmployeePlans] = useState(new Set());
    const [newContentCardDefaults, setNewContentCardDefaults] = useState([]);
    const [newDampingCoefficients, setNewDampingCoefficients] = useState([]);
    const [newEmployeePlans, setNewEmployeePlans] = useState([]);
    const modalContentRef = useRef(null);
    const validationMessageRef = useRef(null);

    const contentViewPlan = useSelector((state) =>
      selectors.selectContentViewPlanForCurrentBoardByYearMonth(
        state,
        contentViewPlanYear,
        contentViewPlanMonth,
      ),
    );

    const pointSystemId = pointSystem?.id;

    const DeletePopup = usePopup(DeleteStep);

    useEffect(() => {
      if (open) {
        onEmployeePlansFetch();
        onContentViewPlanGetOrCreate(contentViewPlanYear, 1);

        if (pointSystemId) {
          onContentCardDefaultsFetch();
          onDampingCoefficientsFetch();
        }

        // Сбрасываем ошибки валидации при открытии модального окна
        setValidationErrors({});
        setDampingValidationErrors({});
        setShowValidationMessage(false);
        setSaveMessage(null);
        setShowCloseConfirmation(false);
        // Сбрасываем состояния для новых и удаленных элементов
        setDeletedContentCardDefaults(new Set());
        setDeletedDampingCoefficients(new Set());
        setDeletedEmployeePlans(new Set());
        setNewContentCardDefaults([]);
        setNewDampingCoefficients([]);
        setNewEmployeePlans([]);
      }
    }, [
      open,
      pointSystemId,
      onEmployeePlansFetch,
      onContentViewPlanGetOrCreate,
      onContentCardDefaultsFetch,
      onDampingCoefficientsFetch,
      contentViewPlanYear,
    ]);

    useEffect(() => {
      if (!contentViewPlan) {
        return;
      }

      const nextPhoto = contentViewPlan.photo ?? 0;
      const nextVideo = contentViewPlan.video ?? 0;
      const nextCaseCount = contentViewPlan.caseCount ?? 0;

      setContentViewPlanForm((prev) => {
        if (
          prev.photo === nextPhoto &&
          prev.video === nextVideo &&
          prev.caseCount === nextCaseCount
        ) {
          return prev;
        }

        return {
          photo: nextPhoto,
          video: nextVideo,
          caseCount: nextCaseCount,
        };
      });
    }, [contentViewPlan]);

    useEffect(() => {
      const ids = new Set(employeePlans.map((plan) => String(plan.id)));

      setDraftsById((prev) => {
        let changed = false;
        let next = prev;

        employeePlans.forEach((plan) => {
          const id = String(plan.id);
          const from = plan.from ?? 0;
          const to = plan.to ?? 0;

          const prevDraft = next[id];
          if (prevDraft && prevDraft.from === from && prevDraft.to === to) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          next[id] = { from, to };
        });

        Object.keys(next).forEach((id) => {
          if (ids.has(id)) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          delete next[id];
        });

        return changed ? next : prev;
      });
    }, [employeePlans]);

    const contentCardDefaults = useSelector((state) =>
      selectors.selectContentCardDefaultsForPointSystem(state, pointSystemId),
    );

    const contentCardDefaultsByType = useMemo(() => {
      const photo = [];
      const video = [];
      const caseItems = [];

      // Добавляем существующие элементы (исключая удаленные)
      contentCardDefaults.forEach((item) => {
        const id = String(item.id);
        if (deletedContentCardDefaults.has(id)) {
          return;
        }
        if (item.type === 'photo') {
          photo.push(item);
        } else if (item.type === 'video') {
          video.push(item);
        } else if (item.type === 'case') {
          caseItems.push(item);
        }
      });

      // Добавляем новые элементы
      newContentCardDefaults.forEach((newItem) => {
        if (newItem.type === 'photo') {
          photo.push({ id: newItem.tempId, type: 'photo', name: '', value: 0, description: '' });
        } else if (newItem.type === 'video') {
          video.push({ id: newItem.tempId, type: 'video', name: '', value: 0, description: '' });
        } else if (newItem.type === 'case') {
          caseItems.push({ id: newItem.tempId, type: 'case', name: '', value: 0, description: '' });
        }
      });

      return {
        photo,
        video,
        case: caseItems,
      };
    }, [contentCardDefaults, deletedContentCardDefaults, newContentCardDefaults]);

    useEffect(() => {
      const items = contentCardDefaults;
      const ids = new Set(items.map((item) => String(item.id)));

      setContentCardDefaultDraftsById((prev) => {
        let changed = false;
        let next = prev;

        // Add drafts for newly appeared items (do not overwrite existing drafts)
        items.forEach((item) => {
          const id = String(item.id);
          if (next[id]) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          next[id] = {
            name: item.name ?? '',
            value: item.value ?? 0,
            description: item.description ?? '',
          };
        });

        // Remove drafts for items that no longer exist
        Object.keys(next).forEach((id) => {
          if (ids.has(id)) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          delete next[id];
        });

        return changed ? next : prev;
      });
    }, [contentCardDefaults]);

    // Прокручиваем модальное окно в начало при показе сообщения об ошибке
    useEffect(() => {
      if (showValidationMessage) {
        const scrollToTop = () => {
          // Ищем модальное окно через селектор Semantic UI
          const modal = document.querySelector('.ui.modal.active, .ui.modal.visible');
          if (!modal) {
            return;
          }
          
          // Сначала пробуем найти прокручиваемый элемент через сообщение
          if (validationMessageRef.current) {
            // Ищем ближайший прокручиваемый родитель сообщения
            let scrollableParent = validationMessageRef.current.parentElement;
            while (scrollableParent && scrollableParent !== modal) {
              const style = window.getComputedStyle(scrollableParent);
              if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && 
                  scrollableParent.scrollHeight > scrollableParent.clientHeight) {
                scrollableParent.scrollTop = 0;
                return;
              }
              scrollableParent = scrollableParent.parentElement;
            }
          }
          
          // Пробуем через ref напрямую
          if (modalContentRef.current) {
            const element = modalContentRef.current;
            if (element && typeof element.scrollTop !== 'undefined') {
              element.scrollTop = 0;
              return;
            }
          }
          
          // Ищем прокручиваемый контент - ищем все элементы внутри модального окна
          const allElements = Array.from(modal.querySelectorAll('*'));
          allElements.push(modal); // Добавляем сам модальный элемент
          
          for (const el of allElements) {
            const style = window.getComputedStyle(el);
            const hasOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll';
            const isScrollable = el.scrollHeight > el.clientHeight;
            
            if (hasOverflow && isScrollable) {
              el.scrollTop = 0;
              return;
            }
          }
        };
        
        // Используем двойной setTimeout для гарантии, что DOM обновлен
        const timeoutId = setTimeout(() => {
          requestAnimationFrame(() => {
            scrollToTop();
          });
        }, 200);
        
        return () => clearTimeout(timeoutId);
      }
    }, [showValidationMessage]);

    const sortedEmployeePlans = useMemo(() => {
      // Фильтруем удаленные элементы
      const existingPlans = employeePlans
        .filter((plan) => !deletedEmployeePlans.has(String(plan.id)))
        .sort((a, b) => (a.sort || 0) - (b.sort || 0));
      
      // Добавляем новые элементы
      const allPlans = [
        ...existingPlans,
        ...newEmployeePlans.map((newItem) => ({
          id: newItem.tempId,
          sort: newItem.sort,
          from: 0,
          to: 0,
        })),
      ].sort((a, b) => (a.sort || 0) - (b.sort || 0));
      
      return allPlans;
    }, [employeePlans, deletedEmployeePlans, newEmployeePlans]);

    const contentViewPlanYearOptions = useMemo(() => {
      const currentYear = new Date().getFullYear();

      return Array.from({ length: 10 }, (_, i) => {
        const year = currentYear + i;
        return {
          key: year,
          text: `${year}`,
          value: year,
        };
      });
    }, []);

    const contentViewPlanMonthTitles = useMemo(
      () => [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь',
      ],
      [],
    );

    const handleDraftChange = useCallback((id, field, value) => {
      setDraftsById((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
        },
      }));
    }, []);

    const handlePlanDelete = useCallback(
      (id) => {
        // Добавляем в список удаленных (не удаляем сразу на сервере)
        setDeletedEmployeePlans((prev) => new Set([...prev, String(id)]));
        // Удаляем из drafts
        setDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        // Удаляем из новых, если это новый элемент
        setNewEmployeePlans((prev) => prev.filter((item) => String(item.tempId) !== String(id)));
      },
      [],
    );

    const handlePlanAdd = useCallback(() => {
      if (!pointSystemId) {
        return;
      }
      // Создаем только в локальном состоянии
      const tempId = `new-ep-${Date.now()}-${Math.random()}`;
      const maxSort = employeePlans.length > 0 
        ? Math.max(...employeePlans.map((p) => p.sort || 0))
        : 0;
      const newItem = {
        tempId,
        sort: maxSort + 1,
        from: 0,
        to: 0,
      };
      setNewEmployeePlans((prev) => [...prev, newItem]);
      setDraftsById((prev) => ({
        ...prev,
        [tempId]: {
          from: 0,
          to: 0,
        },
      }));
    }, [pointSystemId, employeePlans]);

    const handleContentViewPlanYearChange = useCallback(
      (_, { value }) => {
        setContentViewPlanYear(value);
        setContentViewPlanMonth(1);
        onContentViewPlanGetOrCreate(value, 1);
      },
      [onContentViewPlanGetOrCreate],
    );

    const handleContentViewPlanMonthClick = useCallback(
      (month) => {
        setContentViewPlanMonth(month);
        onContentViewPlanGetOrCreate(contentViewPlanYear, month);
      },
      [contentViewPlanYear, onContentViewPlanGetOrCreate],
    );

    const handleContentViewPlanFieldChange = useCallback((field, value) => {
      setContentViewPlanForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    }, []);

    // Вспомогательная функция для проверки конфликта двух диапазонов
    const checkRangeConflict = useCallback((item1, item2) => {
      const from1 = item1.fromValue ?? -Infinity;
      const to1 = item1.isToInfinity ? Infinity : (item1.toValue ?? Infinity);
      const from2 = item2.fromValue ?? -Infinity;
      const to2 = item2.isToInfinity ? Infinity : (item2.toValue ?? Infinity);
      
      // Диапазоны перекрываются, если:
      // (from1 <= to2) && (from2 <= to1)
      return (from1 <= to2) && (from2 <= to1);
    }, []);

    // Функция для проверки конфликтов диапазонов
    const validateDampingCoefficientConflicts = useCallback((draftsOverride = null) => {
      const draftsToUse = draftsOverride || dampingDraftsById;
      const conflicts = {};
      
      // Собираем все активные элементы (существующие + новые, исключая удаленные)
      const allItems = [];
      
      // Существующие элементы
      dampingCoefficients.forEach((dc) => {
        const id = String(dc.id);
        if (deletedDampingCoefficients.has(id)) {
          return;
        }
        const draft = draftsToUse[id];
        if (!draft) {
          return;
        }
        const fromValue = draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
        const isToInfinity = draft.isInfinity;
        const toValue = isToInfinity 
          ? INFINITY_VALUE 
          : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
        
        if (fromValue !== null || toValue !== null) {
          allItems.push({ id, fromValue, toValue, isToInfinity });
        }
      });
      
      // Новые элементы
      newDampingCoefficients.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = draftsToUse[id];
        if (!draft) {
          return;
        }
        const fromValue = draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
        const isToInfinity = draft.isInfinity;
        const toValue = isToInfinity 
          ? INFINITY_VALUE 
          : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
        
        if (fromValue !== null || toValue !== null) {
          allItems.push({ id, fromValue, toValue, isToInfinity });
        }
      });
      
      // Проверяем конфликты между всеми парами элементов
      for (let i = 0; i < allItems.length; i++) {
        const item1 = allItems[i];
        
        for (let j = i + 1; j < allItems.length; j++) {
          const item2 = allItems[j];
          
          // Проверяем перекрытие диапазонов
          const hasConflict = checkRangeConflict(item1, item2);
          
          if (hasConflict) {
            // Если у item1 бесконечность, то конфликт на стороне item1
            if (item1.isToInfinity) {
              conflicts[item1.id] = true;
            }
            // Если у item2 бесконечность, то конфликт на стороне item2
            if (item2.isToInfinity) {
              conflicts[item2.id] = true;
            }
            // Если оба не бесконечность, но диапазоны перекрываются
            if (!item1.isToInfinity && !item2.isToInfinity) {
              // Конфликт у обоих, если диапазоны перекрываются
              conflicts[item1.id] = true;
              conflicts[item2.id] = true;
            }
          }
        }
      }
      
      return conflicts;
    }, [dampingCoefficients, dampingDraftsById, deletedDampingCoefficients, newDampingCoefficients, checkRangeConflict]);

    const handleDampingFieldChange = useCallback((id, field, value) => {
      setDampingDraftsById((prev) => {
        const currentDraft = prev[id] || {};
        const nextDraft = {
          ...currentDraft,
          [field]: value,
        };
        
        // Валидация: from не может быть больше to (только для полей from и to)
        if (field === 'from' || field === 'to') {
          const fromValue = field === 'from' 
            ? (value === '' || value === null ? null : Number(value)) 
            : (currentDraft.from === '' || currentDraft.from === null ? null : Number(currentDraft.from));
          // Если to - бесконечность, то валидация всегда проходит
          const isToInfinity = currentDraft.isInfinity || (field === 'to' && value === INFINITY_VALUE);
          const toValue = isToInfinity 
            ? null // Бесконечность всегда больше любого числа
            : (field === 'to' 
              ? (value === '' || value === null ? null : Number(value)) 
              : (currentDraft.to === '' || currentDraft.to === null ? null : Number(currentDraft.to)));
          
          // Проверяем валидацию только если оба значения заполнены и to не бесконечность
          const hasError = !isToInfinity && fromValue !== null && toValue !== null && !isNaN(fromValue) && !isNaN(toValue) && fromValue > toValue;
          
          // Обновляем ошибки валидации
          setDampingValidationErrors((prevErrors) => {
            if (hasError) {
              return {
                ...prevErrors,
                [id]: { from: true, to: true },
              };
            } else {
              const nextErrors = { ...prevErrors };
              if (nextErrors[id]) {
                delete nextErrors[id];
              }
              return Object.keys(nextErrors).length > 0 ? nextErrors : {};
            }
          });
        }
        
        const nextDrafts = {
          ...prev,
          [id]: nextDraft,
        };
        
        // Проверяем конфликты с новыми данными сразу
        const conflicts = validateDampingCoefficientConflicts(nextDrafts);
        setDampingConflictErrors(conflicts);
        
        return nextDrafts;
      });
    }, [validateDampingCoefficientConflicts]);
    
    // Проверяем конфликты при изменении dampingDraftsById
    useEffect(() => {
      const conflicts = validateDampingCoefficientConflicts();
      setDampingConflictErrors(conflicts);
    }, [dampingDraftsById, validateDampingCoefficientConflicts]);

    const handleDampingInfinityToggle = useCallback((id, checked) => {
      setDampingDraftsById((prev) => {
        const currentDraft = prev[id] || {};
        const nextDrafts = {
          ...prev,
          [id]: {
            ...currentDraft,
            isInfinity: checked,
            to: checked ? null : (currentDraft.to ?? null),
          },
        };
        
        // Проверяем конфликты с новыми данными сразу
        const conflicts = validateDampingCoefficientConflicts(nextDrafts);
        setDampingConflictErrors(conflicts);
        
        return nextDrafts;
      });
      
      // Сбрасываем ошибки валидации при переключении бесконечности
      setDampingValidationErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        if (nextErrors[id]) {
          delete nextErrors[id];
        }
        return Object.keys(nextErrors).length > 0 ? nextErrors : {};
      });
    }, [validateDampingCoefficientConflicts]);


    useEffect(() => {
      if (!dampingCoefficients || dampingCoefficients.length === 0) {
        setDampingDraftsById({});
        return;
      }

      const nextDrafts = {};
      dampingCoefficients.forEach((dc) => {
        const id = String(dc.id);
        // Преобразуем бесконечность (1000000000) в null для UI
        nextDrafts[id] = {
          from: dc.from ?? null,
          to: isInfinity(dc.to) ? null : (dc.to ?? null),
          value: dc.value ?? 1,
          isInfinity: isInfinity(dc.to), // Флаг для UI
        };
      });

      setDampingDraftsById((prev) => {
        // Обновляем только если данные изменились
        const hasChanges = Object.keys(nextDrafts).some(
          (id) =>
            prev[id]?.from !== nextDrafts[id].from ||
            prev[id]?.to !== nextDrafts[id].to ||
            prev[id]?.value !== nextDrafts[id].value ||
            prev[id]?.isInfinity !== nextDrafts[id].isInfinity,
        );

        return hasChanges ? nextDrafts : prev;
      });
    }, [dampingCoefficients]);

    const handleDampingCoefficientDelete = useCallback(
      (id) => {
        // Добавляем в список удаленных (не удаляем сразу на сервере)
        setDeletedDampingCoefficients((prev) => new Set([...prev, String(id)]));
        // Удаляем из drafts
        setDampingDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        // Удаляем из новых, если это новый элемент
        setNewDampingCoefficients((prev) => prev.filter((item) => String(item.tempId) !== String(id)));
      },
      [],
    );

    const handleDampingCoefficientCreate = useCallback(() => {
      if (!pointSystemId) {
        return;
      }
      // Создаем только в локальном состоянии
      const tempId = `new-${Date.now()}-${Math.random()}`;
      const newItem = {
        tempId,
        from: null,
        to: null,
        value: 1,
      };
      setNewDampingCoefficients((prev) => [...prev, newItem]);
      setDampingDraftsById((prev) => ({
        ...prev,
        [tempId]: {
          from: null,
          to: null,
          value: 1,
          isInfinity: false,
        },
      }));
    }, [pointSystemId]);

    const handleContentCardDefaultDraftChange = useCallback((id, field, value) => {
      setContentCardDefaultDraftsById((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
        },
      }));

      // Сбрасываем ошибку валидации для этого поля
      setValidationErrors((prev) => {
        if (!prev[id]) {
          return prev;
        }
        const next = { ...prev };
        next[id] = {
          ...next[id],
          [field]: false,
        };
        // Если оба поля без ошибок, удаляем запись из errors
        if (!next[id].name && !next[id].value) {
          delete next[id];
        }
        return Object.keys(next).length > 0 ? next : {};
      });

      // Скрываем сообщение об ошибке, если все ошибки исправлены
      if (showValidationMessage) {
        setShowValidationMessage(false);
      }
    }, [showValidationMessage]);

    const handleContentCardDefaultDelete = useCallback(
      (id) => {
        // Добавляем в список удаленных (не удаляем сразу на сервере)
        setDeletedContentCardDefaults((prev) => new Set([...prev, String(id)]));
        // Удаляем из drafts
        setContentCardDefaultDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        // Удаляем из новых, если это новый элемент
        setNewContentCardDefaults((prev) => prev.filter((item) => String(item.tempId) !== String(id)));
      },
      [],
    );

    // Функция для создания нового ContentCardDefault только в локальном состоянии
    const handleContentCardDefaultCreate = useCallback(
      (type) => {
        if (!pointSystemId) {
          return;
        }
        // Создаем только в локальном состоянии
        const tempId = `new-ccd-${Date.now()}-${Math.random()}`;
        const newItem = {
          tempId,
          type,
          name: '',
          value: 0,
          description: '',
        };
        setNewContentCardDefaults((prev) => [...prev, newItem]);
        setContentCardDefaultDraftsById((prev) => ({
          ...prev,
          [tempId]: {
            name: '',
            value: 0,
            description: '',
          },
        }));
      },
      [pointSystemId],
    );

    const validateContentCardDefaults = useCallback(() => {
      const errors = {};
      const idsToDelete = [];

      // Валидируем существующие элементы
      contentCardDefaults.forEach((item) => {
        const id = String(item.id);
        // Пропускаем удаленные
        if (deletedContentCardDefaults.has(id)) {
          return;
        }
        const draft = contentCardDefaultDraftsById[id];
        
        if (!draft) {
          return;
        }

        const name = draft.name;
        const value = draft.value;

        const isNameEmpty = !name || name === '' || name === undefined || name === null;
        const isValueEmpty = value === null || value === undefined || value === 0 || value === '0' || value === '';

        if (isNameEmpty && isValueEmpty) {
          // Оба поля пустые - нужно удалить
          idsToDelete.push(id);
        } else if (isNameEmpty || isValueEmpty) {
          // Одно поле пустое - ошибка валидации
          errors[id] = {
            name: isNameEmpty,
            value: isValueEmpty,
          };
        }
      });

      // Валидируем новые элементы
      newContentCardDefaults.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = contentCardDefaultDraftsById[id];
        
        if (!draft) {
          return;
        }

        const name = draft.name;
        const value = draft.value;

        const isNameEmpty = !name || name === '' || name === undefined || name === null;
        const isValueEmpty = value === null || value === undefined || value === 0 || value === '0' || value === '';

        if (isNameEmpty && isValueEmpty) {
          // Оба поля пустые - удаляем из новых
          idsToDelete.push(id);
        } else if (isNameEmpty || isValueEmpty) {
          // Одно поле пустое - ошибка валидации
          errors[id] = {
            name: isNameEmpty,
            value: isValueEmpty,
          };
        }
      });

      return { errors, idsToDelete };
    }, [contentCardDefaults, contentCardDefaultDraftsById, deletedContentCardDefaults, newContentCardDefaults]);

    const validateDampingCoefficients = useCallback(() => {
      const errors = {};

      // Валидируем существующие элементы
      dampingCoefficients.forEach((dc) => {
        const id = String(dc.id);
        // Пропускаем удаленные
        if (deletedDampingCoefficients.has(id)) {
          return;
        }
        const draft = dampingDraftsById[id];
        
        if (!draft) {
          return;
        }

        const fromValue = draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
        const isToInfinity = draft.isInfinity;
        const toValue = isToInfinity 
          ? null // Бесконечность всегда больше любого числа
          : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);

        // Проверяем, что from не больше to (только если оба значения заполнены и to не бесконечность)
        if (!isToInfinity && fromValue !== null && toValue !== null && fromValue > toValue) {
          errors[id] = { from: true, to: true };
        }
      });

      // Валидируем новые элементы
      newDampingCoefficients.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = dampingDraftsById[id];
        
        if (!draft) {
          return;
        }

        const fromValue = draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
        const isToInfinity = draft.isInfinity;
        const toValue = isToInfinity 
          ? null // Бесконечность всегда больше любого числа
          : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);

        // Проверяем, что from не больше to (только если оба значения заполнены и to не бесконечность)
        if (!isToInfinity && fromValue !== null && toValue !== null && fromValue > toValue) {
          errors[id] = { from: true, to: true };
        }
      });

      return errors;
    }, [dampingCoefficients, dampingDraftsById, deletedDampingCoefficients, newDampingCoefficients]);

    // Функция для проверки наличия изменений в данных
    const hasUnsavedChanges = useCallback(() => {
      // Проверяем изменения в ContentCardDefaults
      const hasContentCardDefaultChanges = contentCardDefaults.some((item) => {
        const id = String(item.id);
        if (deletedContentCardDefaults.has(id)) {
          return true;
        }
        const draft = contentCardDefaultDraftsById[id];
        if (draft) {
          return (
            draft.name !== (item.name ?? '') ||
            draft.value !== (item.value ?? 0) ||
            draft.description !== (item.description ?? '')
          );
        }
        return false;
      });

      // Проверяем новые ContentCardDefaults
      const hasNewContentCardDefaults = newContentCardDefaults.length > 0;

      // Проверяем изменения в DampingCoefficients
      const hasDampingChanges = dampingCoefficients.some((dc) => {
        const id = String(dc.id);
        if (deletedDampingCoefficients.has(id)) {
          return true;
        }
        const draft = dampingDraftsById[id];
        if (draft) {
          const draftFrom = draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
          const draftTo = draft.isInfinity 
            ? INFINITY_VALUE 
            : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
          const draftValue = Number(draft.value) || 1;
          const originalFrom = dc.from !== null && dc.from !== '' ? Number(dc.from) : null;
          const originalTo = isInfinity(dc.to) ? INFINITY_VALUE : (dc.to !== null && dc.to !== '' ? Number(dc.to) : null);
          const originalValue = Number(dc.value) || 1;
          
          return (
            draftFrom !== originalFrom ||
            draftTo !== originalTo ||
            draftValue !== originalValue
          );
        }
        return false;
      });

      // Проверяем новые DampingCoefficients
      const hasNewDampingCoefficients = newDampingCoefficients.length > 0;

      // Проверяем изменения в ContentViewPlan
      const hasContentViewPlanChanges = contentViewPlan
        ? contentViewPlanForm.photo !== (contentViewPlan.photo ?? 0) ||
          contentViewPlanForm.video !== (contentViewPlan.video ?? 0) ||
          contentViewPlanForm.caseCount !== (contentViewPlan.caseCount ?? 0)
        : false;

      // Проверяем изменения в EmployeePlans
      const hasEmployeePlanChanges = employeePlans.some((plan) => {
        const id = String(plan.id);
        if (deletedEmployeePlans.has(id)) {
          return true;
        }
        const draft = draftsById[id];
        if (draft) {
          return draft.from !== (plan.from ?? 0) || draft.to !== (plan.to ?? 0);
        }
        return false;
      });

      // Проверяем новые EmployeePlans
      const hasNewEmployeePlans = newEmployeePlans.length > 0;

      return (
        hasContentCardDefaultChanges ||
        hasNewContentCardDefaults ||
        hasDampingChanges ||
        hasNewDampingCoefficients ||
        hasContentViewPlanChanges ||
        hasEmployeePlanChanges ||
        hasNewEmployeePlans ||
        deletedContentCardDefaults.size > 0 ||
        deletedDampingCoefficients.size > 0 ||
        deletedEmployeePlans.size > 0
      );
    }, [
      contentCardDefaults,
      contentCardDefaultDraftsById,
      deletedContentCardDefaults,
      newContentCardDefaults,
      dampingCoefficients,
      dampingDraftsById,
      deletedDampingCoefficients,
      newDampingCoefficients,
      contentViewPlan,
      contentViewPlanForm,
      employeePlans,
      draftsById,
      deletedEmployeePlans,
      newEmployeePlans,
    ]);

    const handleCloseConfirm = useCallback(() => {
      setShowCloseConfirmation(false);
      
      const { errors, idsToDelete } = validateContentCardDefaults();

      // Удаляем записи, где оба поля пустые
      idsToDelete.forEach((id) => {
        onContentCardDefaultDelete(id);
        // Удаляем ошибки для удаляемых записей
        delete errors[id];
      });

      // Если есть ошибки валидации - показываем их и не закрываем модалку
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setShowValidationMessage(true);
        return;
      }

      // Если все ок - закрываем модалку
      setValidationErrors({});
      setShowValidationMessage(false);
      onClose();
    }, [validateContentCardDefaults, onContentCardDefaultDelete, onClose]);

    const handleCloseWithValidation = useCallback(() => {
      // Проверяем наличие несохраненных изменений
      if (hasUnsavedChanges()) {
        // Показываем подтверждение
        setShowCloseConfirmation(true);
        return;
      }

      handleCloseConfirm();
    }, [hasUnsavedChanges, handleCloseConfirm]);

    const renderContentCardDefaults = useCallback(
      (items, type) => {
        if (!pointSystem) {
          return <div className={styles.emptyHint}>Сначала создайте балльную систему для доски</div>;
        }

        return (
          <>
            {!items.length ? (
              <div className={styles.emptyHint}>Нет записей</div>
            ) : (
              <div className={styles.contentCardDefaultsList}>
                {items.map((item) => {
                  const itemId = String(item.id);
                  const hasNameError = validationErrors[itemId]?.name;
                  const hasValueError = validationErrors[itemId]?.value;

                  return (
                    <Form key={item.id} className={styles.contentCardDefaultRow}>
                      <Form.Field inline className={styles.contentCardDefaultFieldName}>
                        <label>Название</label>
                        <input
                          className={classNames({ [styles.fieldError]: hasNameError })}
                          value={contentCardDefaultDraftsById[item.id]?.name ?? ''}
                          onChange={(e) =>
                            handleContentCardDefaultDraftChange(item.id, 'name', e.target.value)
                          }
                        />
                      </Form.Field>
                      <Form.Field inline className={styles.contentCardDefaultFieldValue}>
                        <label>Баллы</label>
                        <input
                          type="number"
                          className={classNames({ [styles.fieldError]: hasValueError })}
                          value={contentCardDefaultDraftsById[item.id]?.value ?? 0}
                          onChange={(e) =>
                            handleContentCardDefaultDraftChange(item.id, 'value', e.target.value)
                          }
                        />
                      </Form.Field>
                    <Form.Field inline className={styles.contentCardDefaultFieldDescription}>
                      <label>Описание</label>
                      <input
                        value={contentCardDefaultDraftsById[item.id]?.description ?? ''}
                        onChange={(e) =>
                          handleContentCardDefaultDraftChange(item.id, 'description', e.target.value)
                        }
                      />
                    </Form.Field>
                    <DeletePopup
                      title="Удаление"
                      content="Удалить запись?"
                      buttonContent="Удалить"
                      onConfirm={() => handleContentCardDefaultDelete(item.id)}
                    >
                      <Button
                        type="button"
                        icon
                        basic
                        size="mini"
                        className={styles.contentCardDefaultDeleteButton}
                      >
                        <Icon name="trash" />
                      </Button>
                    </DeletePopup>
                  </Form>
                  );
                })}
              </div>
            )}

            <div className={styles.contentCardDefaultsActions}>
              <Button type="button" positive content="Добавить" onClick={() => handleContentCardDefaultCreate(type)} />
            </div>
          </>
        );
      },
      [
        pointSystem,
        contentCardDefaultDraftsById,
        validationErrors,
        handleContentCardDefaultDraftChange,
        handleContentCardDefaultDelete,
        handleContentCardDefaultCreate,
      ],
    );

    const contentCardDefaultsPanes = useMemo(
      () => [
        {
          menuItem: 'Фотография',
          render: () => <Tab.Pane>{renderContentCardDefaults(contentCardDefaultsByType.photo, 'photo')}</Tab.Pane>,
        },
        {
          menuItem: 'Видео',
          render: () => <Tab.Pane>{renderContentCardDefaults(contentCardDefaultsByType.video, 'video')}</Tab.Pane>,
        },
        {
          menuItem: 'Кейсы',
          render: () => <Tab.Pane>{renderContentCardDefaults(contentCardDefaultsByType.case, 'case')}</Tab.Pane>,
        },
      ],
      [contentCardDefaultsByType, renderContentCardDefaults],
    );

    // Функция сохранения всех изменений
    const handleSave = useCallback(async () => {
      if (!pointSystemId) {
        return;
      }

      // Валидация ContentCardDefaults
      const { errors, idsToDelete } = validateContentCardDefaults();

      // Добавляем пустые записи в список удаленных
      idsToDelete.forEach((id) => {
        setDeletedContentCardDefaults((prev) => new Set([...prev, String(id)]));
      });

      // Валидация DampingCoefficients
      const dampingErrors = validateDampingCoefficients();
      setDampingValidationErrors(dampingErrors);
      
      // Проверка конфликтов диапазонов
      const conflicts = validateDampingCoefficientConflicts();
      setDampingConflictErrors(conflicts);

      // Если есть ошибки валидации или конфликты - показываем их и не сохраняем
      if (Object.keys(errors).length > 0 || Object.keys(dampingErrors).length > 0 || Object.keys(conflicts).length > 0) {
        setValidationErrors(errors);
        setShowValidationMessage(true);
        return;
      }

      try {
        // Удаляем ContentCardDefaults
        deletedContentCardDefaults.forEach((id) => {
          // Проверяем, что это не новый элемент
          if (!id.startsWith('new-')) {
            onContentCardDefaultDelete(id);
          }
        });

        // Обновляем существующие ContentCardDefaults
        contentCardDefaults.forEach((item) => {
          const id = String(item.id);
          // Пропускаем удаленные
          if (deletedContentCardDefaults.has(id)) {
            return;
          }
          const draft = contentCardDefaultDraftsById[id];
          if (draft) {
            // Проверяем, есть ли изменения
            const hasChanges =
              draft.name !== (item.name ?? '') ||
              draft.value !== (item.value ?? 0) ||
              draft.description !== (item.description ?? '');
            if (hasChanges) {
              onContentCardDefaultUpdate(id, {
                name: draft.name,
                value: Number(draft.value) || 0,
                description: draft.description,
              });
            }
          }
        });

        // Создаем новые ContentCardDefaults
        newContentCardDefaults.forEach((newItem) => {
          const draft = contentCardDefaultDraftsById[newItem.tempId];
          if (draft && draft.name && draft.value) {
            onContentCardDefaultCreate(newItem.type, {
              name: draft.name,
              value: Number(draft.value) || 0,
              description: draft.description || '',
            });
          }
        });

        // Удаляем DampingCoefficients
        deletedDampingCoefficients.forEach((id) => {
          // Проверяем, что это не новый элемент
          if (!id.startsWith('new-')) {
            onDampingCoefficientDelete(id);
          }
        });

        // Обновляем существующие DampingCoefficients
        dampingCoefficients.forEach((dc) => {
          const id = String(dc.id);
          // Пропускаем удаленные
          if (deletedDampingCoefficients.has(id)) {
            return;
          }
          const draft = dampingDraftsById[id];
          if (draft) {
            // Преобразуем значения для корректного сравнения
            const draftFrom = draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
            // Если бесконечность, преобразуем null в INFINITY_VALUE
            const draftTo = draft.isInfinity 
              ? INFINITY_VALUE 
              : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
            const draftValue = Number(draft.value) || 1;
            const originalFrom = dc.from !== null && dc.from !== '' ? Number(dc.from) : null;
            const originalTo = isInfinity(dc.to) ? INFINITY_VALUE : (dc.to !== null && dc.to !== '' ? Number(dc.to) : null);
            const originalValue = Number(dc.value) || 1;
            
            // Проверяем, есть ли изменения
            const hasChanges =
              draftFrom !== originalFrom ||
              draftTo !== originalTo ||
              draftValue !== originalValue;
            if (hasChanges) {
              onDampingCoefficientUpdate(id, {
                from: draftFrom,
                to: draftTo,
                value: draftValue,
              });
            }
          }
        });

        // Создаем новые DampingCoefficients
        newDampingCoefficients.forEach((newItem) => {
          const draft = dampingDraftsById[newItem.tempId];
          if (draft) {
            // Если бесконечность, преобразуем null в INFINITY_VALUE
            const toValue = draft.isInfinity 
              ? INFINITY_VALUE 
              : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
            onDampingCoefficientCreate(pointSystemId, {
              from: draft.from !== null && draft.from !== '' ? Number(draft.from) : null,
              to: toValue,
              value: Number(draft.value) || 1,
            });
          }
        });

        // Обновляем ContentViewPlan
        if (contentViewPlan) {
          const hasChanges =
            contentViewPlanForm.photo !== (contentViewPlan.photo ?? 0) ||
            contentViewPlanForm.video !== (contentViewPlan.video ?? 0) ||
            contentViewPlanForm.caseCount !== (contentViewPlan.caseCount ?? 0);
          if (hasChanges) {
            onContentViewPlanUpdate(contentViewPlan.id, {
              photo: Number(contentViewPlanForm.photo) || 0,
              video: Number(contentViewPlanForm.video) || 0,
              caseCount: Number(contentViewPlanForm.caseCount) || 0,
            });
          }
        }

        // Удаляем EmployeePlans
        deletedEmployeePlans.forEach((id) => {
          // Проверяем, что это не новый элемент
          if (!id.startsWith('new-')) {
            onEmployeePlanDelete(id);
          }
        });

        // Обновляем существующие EmployeePlans
        employeePlans.forEach((plan) => {
          const id = String(plan.id);
          // Пропускаем удаленные
          if (deletedEmployeePlans.has(id)) {
            return;
          }
          const draft = draftsById[id];
          if (draft) {
            const hasChanges =
              draft.from !== (plan.from ?? 0) || draft.to !== (plan.to ?? 0);
            if (hasChanges) {
              onEmployeePlanUpdate(id, {
                from: Number(draft.from) || 0,
                to: Number(draft.to) || 0,
              });
            }
          }
        });

        // Создаем новые EmployeePlans
        newEmployeePlans.forEach((newItem) => {
          const draft = draftsById[newItem.tempId];
          if (draft) {
            onEmployeePlanCreate({
              from: Number(draft.from) || 0,
              to: Number(draft.to) || 0,
              sort: newItem.sort,
            });
          }
        });

        // Очищаем состояния после успешного сохранения
        setDeletedContentCardDefaults(new Set());
        setDeletedDampingCoefficients(new Set());
        setDeletedEmployeePlans(new Set());
        setNewContentCardDefaults([]);
        setNewDampingCoefficients([]);
        setNewEmployeePlans([]);
        setValidationErrors({});
        setDampingValidationErrors({});
        setShowValidationMessage(false);
        
        // Показываем сообщение об успехе
        setSaveMessage({ type: 'success', text: 'Данные успешно сохранены' });
        
        // Автоматически скрываем сообщение через 3 секунды
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } catch (error) {
        console.error('Ошибка при сохранении:', error);
        
        // Показываем сообщение об ошибке
        const errorText = error?.message || 'Произошла ошибка при сохранении данных';
        setSaveMessage({ type: 'error', text: errorText });
        
        // Автоматически скрываем сообщение через 5 секунд
        setTimeout(() => {
          setSaveMessage(null);
        }, 5000);
      }
    }, [
      pointSystemId,
      validateContentCardDefaults,
      validateDampingCoefficients,
      deletedContentCardDefaults,
      deletedDampingCoefficients,
      deletedEmployeePlans,
      newContentCardDefaults,
      newDampingCoefficients,
      newEmployeePlans,
      contentCardDefaults,
      contentCardDefaultDraftsById,
      dampingCoefficients,
      dampingDraftsById,
      contentViewPlan,
      contentViewPlanForm,
      employeePlans,
      draftsById,
      onContentCardDefaultDelete,
      onContentCardDefaultUpdate,
      onContentCardDefaultCreate,
      onDampingCoefficientDelete,
      onDampingCoefficientUpdate,
      onDampingCoefficientCreate,
      onContentViewPlanUpdate,
      onEmployeePlanUpdate,
    ]);

    return (
      <>
        <Modal
          open={open && !showCloseConfirmation}
          closeIcon
          size="large"
          centered={false}
          className={styles.modal}
          closeOnDimmerClick={false}
          onClose={handleCloseWithValidation}
        >
          <Modal.Header>
        <span>Настройки балльной системы</span>
      </Modal.Header>
    <Modal.Content className={styles.content}>
      <div ref={modalContentRef}>
      {showValidationMessage && (
        <Message ref={validationMessageRef} negative style={{ margin: 0, borderRadius: 0 }}>
          <Message.Header>Ошибка</Message.Header>
          <p>
            {Object.keys(validationErrors).length > 0 && 'В секции ТИП КОНТЕНТА есть незаполненные обязательные поля'}
            {Object.keys(validationErrors).length > 0 && (Object.keys(dampingValidationErrors).length > 0 || Object.keys(dampingConflictErrors).length > 0) && '. '}
            {Object.keys(dampingValidationErrors).length > 0 && 'В секции НАСТРОЙКА ДЕМПФИРУЮЩЕГО КОЭФФИЦИЕНТА значение "от" не может быть больше значения "до"'}
            {Object.keys(dampingValidationErrors).length > 0 && Object.keys(dampingConflictErrors).length > 0 && '. '}
            {Object.keys(dampingConflictErrors).length > 0 && 'В секции НАСТРОЙКА ДЕМПФИРУЮЩЕГО КОЭФФИЦИЕНТА есть конфликты диапазонов'}
          </p>
        </Message>
      )}
      <div className={styles.sections}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>Планы сотрудников</div>
              <SectionInfo title="Планы сотрудников" />
            </div>
          </div>
          <div className={styles.sectionBody}>
            {!pointSystem ? (
              <div className={styles.emptyHint}>Сначала создайте балльную систему для доски</div>
            ) : (
              <>
                <div className={styles.employeePlansList}>
                  {sortedEmployeePlans.map((plan) => (
                    <Form key={plan.id} className={styles.employeePlanRow}>
                      <div className={styles.employeePlanLevel}>{`${plan.sort} уровень`}</div>

                      <Form.Field inline className={styles.employeePlanField}>
                        <label>От</label>
                        <input
                          type="number"
                          value={draftsById[plan.id]?.from ?? 0}
                          onChange={(e) => handleDraftChange(plan.id, 'from', e.target.value)}
                        />
                      </Form.Field>

                      <Form.Field inline className={styles.employeePlanField}>
                        <label>До</label>
                        <input
                          type="number"
                          value={draftsById[plan.id]?.to ?? 0}
                          onChange={(e) => handleDraftChange(plan.id, 'to', e.target.value)}
                        />
                      </Form.Field>

                      <DeletePopup
                        title="Удаление"
                        content="Удалить элемент?"
                        buttonContent="Удалить"
                        onConfirm={() => handlePlanDelete(plan.id)}
                      >
                        <Button
                          type="button"
                          icon
                          basic
                          color="red"
                          size="mini"
                          className={styles.employeePlanDeleteButton}
                        >
                          <Icon name="trash" />
                        </Button>
                      </DeletePopup>
                    </Form>
                  ))}
                </div>

                <div className={styles.employeePlansActions}>
                  <Button positive content="Добавить" onClick={handlePlanAdd} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>ПЛАН ДЛЯ ПРОСМОТРОВ КОНТЕНТА</div>
              <SectionInfo title="ПЛАН ДЛЯ ПРОСМОТРОВ КОНТЕНТА" />
            </div>
          </div>
          <div className={styles.sectionBody}>
            <div className={styles.contentViewPlanBlock}>
              <div className={styles.contentViewPlanBlockTitle}>Календарь</div>
              <Form>
                <Form.Select
                  label="Год"
                  options={contentViewPlanYearOptions}
                  value={contentViewPlanYear}
                  onChange={handleContentViewPlanYearChange}
                />
              </Form>
            </div>

            <div className={styles.contentViewPlanBlock}>
              <div className={styles.contentViewPlanBlockTitle}>Месяц</div>
              <div className={styles.monthButtonsGrid}>
                {contentViewPlanMonthTitles.map((title, idx) => {
                  const month = idx + 1;
                  const isActive = contentViewPlanMonth === month;

                  return (
                    <Button
                      // eslint-disable-next-line react/no-array-index-key
                      key={title}
                      type="button"
                      size="small"
                      basic={!isActive}
                      primary={isActive}
                      className={styles.monthButton}
                      onClick={() => handleContentViewPlanMonthClick(month)}
                    >
                      {title}
                    </Button>
                  );
                })}
              </div>

              <div className={styles.contentViewPlanFields}>
                <Form>
                  <div className={styles.contentViewPlanFieldsGrid}>
                    <Form.Field inline>
                      <label>Фотография</label>
                      <input
                        type="number"
                        value={contentViewPlanForm.photo}
                        onChange={(e) => handleContentViewPlanFieldChange('photo', e.target.value)}
                      />
                    </Form.Field>
                    <Form.Field inline>
                      <label>Видео</label>
                      <input
                        type="number"
                        value={contentViewPlanForm.video}
                        onChange={(e) => handleContentViewPlanFieldChange('video', e.target.value)}
                      />
                    </Form.Field>
                    <Form.Field inline>
                      <label>Кейсы</label>
                      <input
                        type="number"
                        value={contentViewPlanForm.caseCount}
                        onChange={(e) =>
                          handleContentViewPlanFieldChange('caseCount', e.target.value)
                        }
                      />
                    </Form.Field>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>Настройка демпфирующего коэффициента</div>
              <SectionInfo title="Настройка демпфирующего коэффициента" />
            </div>
          </div>
          <div className={styles.sectionBody}>
            {(dampingCoefficients.length > 0 || newDampingCoefficients.length > 0) && (
              <div className={styles.dampingBlock}>
                {/* Существующие элементы (исключая удаленные) */}
                {dampingCoefficients
                  .filter((dc) => !deletedDampingCoefficients.has(String(dc.id)))
                  .map((dc) => {
                    const id = String(dc.id);
                    const draft = dampingDraftsById[id] || {
                      from: dc.from ?? null,
                      to: isInfinity(dc.to) ? null : (dc.to ?? null),
                      value: dc.value ?? 1,
                      isInfinity: isInfinity(dc.to),
                    };
                    const hasFromError = dampingValidationErrors[id]?.from;
                    const hasToError = dampingValidationErrors[id]?.to;
                    const hasConflictError = dampingConflictErrors[id];

                    return (
                      <Form key={dc.id} className={styles.dampingItem}>
                      <Form.Field inline className={styles.dampingField}>
                        <label>от (%)</label>
                        <input
                          type="number"
                          step="any"
                          className={classNames({ [styles.fieldError]: hasFromError })}
                          value={draft.from ?? ''}
                          onChange={(e) =>
                            handleDampingFieldChange(
                              id,
                              'from',
                              e.target.value === '' ? null : e.target.value,
                            )
                          }
                          placeholder="Не указано"
                        />
                      </Form.Field>
                      <Form.Field inline className={classNames(styles.dampingField, styles.dampingFieldWithToggle)}>
                        <label>до (%)</label>
                        <div className={styles.dampingToWrapper}>
                          <div className={styles.dampingToInputWrapper}>
                            {draft.isInfinity ? (
                              <div className={styles.infinityIconWrapper}>
                                <InfinityIcon />
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="any"
                                className={classNames({ [styles.fieldError]: hasToError })}
                                value={draft.to ?? ''}
                                onChange={(e) =>
                                  handleDampingFieldChange(
                                    id,
                                    'to',
                                    e.target.value === '' ? null : e.target.value,
                                  )
                                }
                                placeholder="Не указано"
                              />
                            )}
                          </div>
                          <InfinityToggle
                            checked={draft.isInfinity}
                            onChange={(checked) => handleDampingInfinityToggle(id, checked)}
                          />
                        </div>
                      </Form.Field>
                      <Form.Field inline className={styles.dampingField}>
                        <label>Коэффициент</label>
                        <input
                          type="number"
                          step="any"
                          value={draft.value ?? 1}
                          onChange={(e) => handleDampingFieldChange(id, 'value', e.target.value)}
                        />
                      </Form.Field>
                      <Button
                        type="button"
                        icon="trash"
                        negative
                        className={styles.dampingDeleteButton}
                        onClick={() => handleDampingCoefficientDelete(id)}
                      />
                      {hasConflictError && draft.isInfinity && (
                        <div className={styles.conflictError}>
                          Конфликт с другими значениями
                        </div>
                      )}
                    </Form>
                  );
                })}
                {/* Новые элементы */}
                {newDampingCoefficients.map((newItem) => {
                  const id = String(newItem.tempId);
                  const draft = dampingDraftsById[id] || {
                    from: null,
                    to: null,
                    value: 1,
                    isInfinity: false,
                  };
                  const hasFromError = dampingValidationErrors[id]?.from;
                  const hasToError = dampingValidationErrors[id]?.to;
                  const hasConflictError = dampingConflictErrors[id];

                  return (
                    <Form key={newItem.tempId} className={styles.dampingItem}>
                      <Form.Field inline className={styles.dampingField}>
                        <label>от (%)</label>
                        <input
                          type="number"
                          step="any"
                          className={classNames({ [styles.fieldError]: hasFromError })}
                          value={draft.from ?? ''}
                          onChange={(e) =>
                            handleDampingFieldChange(
                              id,
                              'from',
                              e.target.value === '' ? null : e.target.value,
                            )
                          }
                          placeholder="Не указано"
                        />
                      </Form.Field>
                      <Form.Field inline className={classNames(styles.dampingField, styles.dampingFieldWithToggle)}>
                        <label>до (%)</label>
                        <div className={styles.dampingToWrapper}>
                          <div className={styles.dampingToInputWrapper}>
                            {draft.isInfinity ? (
                              <div className={styles.infinityIconWrapper}>
                                <InfinityIcon />
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="any"
                                className={classNames({ [styles.fieldError]: hasToError })}
                                value={draft.to ?? ''}
                                onChange={(e) =>
                                  handleDampingFieldChange(
                                    id,
                                    'to',
                                    e.target.value === '' ? null : e.target.value,
                                  )
                                }
                                placeholder="Не указано"
                              />
                            )}
                          </div>
                          <InfinityToggle
                            checked={draft.isInfinity}
                            onChange={(checked) => handleDampingInfinityToggle(id, checked)}
                          />
                        </div>
                      </Form.Field>
                      <Form.Field inline className={styles.dampingField}>
                        <label>Коэффициент</label>
                        <input
                          type="number"
                          step="any"
                          value={draft.value ?? 1}
                          onChange={(e) => handleDampingFieldChange(newItem.tempId, 'value', e.target.value)}
                        />
                      </Form.Field>
                      <Button
                        type="button"
                        icon="trash"
                        negative
                        className={styles.dampingDeleteButton}
                        onClick={() => handleDampingCoefficientDelete(newItem.tempId)}
                      />
                      {hasConflictError && draft.isInfinity && (
                        <div className={styles.conflictError}>
                          Конфликт с другими значениями
                        </div>
                      )}
                    </Form>
                  );
                })}
              </div>
            )}
            <div className={styles.dampingBlock}>
              <Button type="button" positive content="Добавить коэффициент" onClick={handleDampingCoefficientCreate} />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>
                ТИП КОНТЕНТА (БАЛЛЫ ЗА ЗАДАЧУ ПО УМОЛЧАНИЮ)
              </div>
              <SectionInfo title="ТИП КОНТЕНТА (БАЛЛЫ ЗА ЗАДАЧУ ПО УМОЛЧАНИЮ)" />
            </div>
          </div>
          <div className={styles.sectionBody}>
            <Tab
              className={styles.contentCardDefaultsTabs}
              menu={{
                secondary: true,
                pointing: true,
              }}
              panes={contentCardDefaultsPanes}
            />
          </div>
        </div>
      </div>
      </div>
    </Modal.Content>
    <Modal.Actions className={styles.modalActions}>
      {saveMessage && (
        <div className={classNames(styles.saveMessage, { [styles.error]: saveMessage.type === 'error' })}>
          {saveMessage.text}
        </div>
      )}
      <div className={styles.saveButtonWrapper}>
        <Button positive onClick={handleSave} size="small">
          Сохранить
        </Button>
      </div>
    </Modal.Actions>
  </Modal>
  <Modal
    open={showCloseConfirmation}
    size="small"
    centered={false}
    closeOnDimmerClick={false}
    onClose={() => setShowCloseConfirmation(false)}
  >
    <Modal.Header>Подтверждение</Modal.Header>
    <Modal.Content>
      <div style={{ marginBottom: '20px' }}>
        У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно без сохранения?
      </div>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={() => setShowCloseConfirmation(false)}>
        Отмена
      </Button>
      <Button negative onClick={handleCloseConfirm}>
        Закрыть без сохранения
      </Button>
    </Modal.Actions>
  </Modal>
      </>
    );
  },
);

PointSystemModal.propTypes = {
  open: PropTypes.bool,
  /* eslint-disable react/forbid-prop-types */
  pointSystem: PropTypes.object,
  dampingCoefficients: PropTypes.array,
  employeePlans: PropTypes.array,
  /* eslint-enable react/forbid-prop-types */
  onEmployeePlansFetch: PropTypes.func.isRequired,
  onEmployeePlanCreate: PropTypes.func.isRequired,
  onEmployeePlanUpdate: PropTypes.func.isRequired,
  onEmployeePlanDelete: PropTypes.func.isRequired,
  onContentViewPlanGetOrCreate: PropTypes.func.isRequired,
  onContentViewPlanUpdate: PropTypes.func.isRequired,
  onContentCardDefaultsFetch: PropTypes.func.isRequired,
  onContentCardDefaultCreate: PropTypes.func.isRequired,
  onContentCardDefaultUpdate: PropTypes.func.isRequired,
  onContentCardDefaultDelete: PropTypes.func.isRequired,
  onDampingCoefficientsFetch: PropTypes.func.isRequired,
  onDampingCoefficientCreate: PropTypes.func.isRequired,
  onDampingCoefficientUpdate: PropTypes.func.isRequired,
  onDampingCoefficientDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

PointSystemModal.defaultProps = {
  open: true,
  pointSystem: null,
  dampingCoefficients: [],
  employeePlans: [],
};

export default PointSystemModal;

